/**
 * Read-side helpers for the cached records list (infinite query pages).
 *
 * The list rows carry almost everything the detail header needs, so the
 * detail panel seeds itself from here (placeholderData) instead of blanking
 * to a skeleton while GET /records/:id is in flight.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { RecordListItem } from '@aica/api-types';

interface RecordsListPage {
  records?: RecordListItem[];
}

interface InfiniteRecordsData {
  pages?: RecordsListPage[];
}

/**
 * Find a record's list-item snapshot in ANY cached records list query
 * (['records', 'infinite', ...] — all filter combinations and orgs share the
 * prefix; rows are org-scoped by id so a cross-org match cannot occur).
 * Returns undefined when the record is not in any cached page (e.g. a deep
 * link opened before the list ever loaded).
 */
export function findRecordListItem(
  queryClient: QueryClient,
  recordId: string
): RecordListItem | undefined {
  const entries = queryClient.getQueriesData<InfiniteRecordsData>({
    queryKey: ['records', 'infinite'],
  });

  for (const [, data] of entries) {
    for (const page of data?.pages ?? []) {
      const match = page.records?.find((record) => record.id === recordId);
      if (match) {
        return match;
      }
    }
  }

  return undefined;
}
